import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { api } from "../../lib/api";
import { formatDate } from "../../lib/datetime";
import { toast } from "sonner";
import { CalendarClock, AlertTriangle, CheckCircle2, Download, ExternalLink, Mail, Phone, Building2, User, Tag } from "lucide-react";
import { useConfirm } from "../../components/ConfirmDialog";
import { useCompany } from "../../context/CompanyContext";
import { CompanySwitcher } from "./AdminLayout";

const STAGE_LABEL = {
  nuevo: "Nuevo",
  contactado: "Contactado",
  calificado: "Calificado",
  propuesta: "Propuesta",
  negociacion: "Negociación",
  ganado: "Ganado",
  perdido: "Perdido",
};
const STAGE_COLOR = {
  nuevo: "#9BA7B3",
  contactado: "#7BA6B0",
  calificado: "#5B8FAF",
  propuesta: "#D97757",
  negociacion: "#D9A557",
  ganado: "#1B7E3F",
  perdido: "#9B1C1C",
};

const fmtMoney = (v, c) => {
  if (!v) return "—";
  return c === "CLP"
    ? `$${Math.round(v).toLocaleString("es-CL")}`
    : `${c === "USD" ? "US$" : c === "EUR" ? "€" : ""}${Number(v).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

// Compare dates in Chile TZ — returns diff in calendar days (today = 0)
const daysFromToday = (iso) => {
  if (!iso) return null;
  const target = new Date(iso);
  if (isNaN(target.getTime())) return null;
  const fmt = (d) => d.toLocaleDateString("en-CA", { timeZone: "America/Santiago" }); // YYYY-MM-DD
  const todayStr = fmt(new Date());
  const targetStr = fmt(target);
  const a = new Date(todayStr + "T00:00:00Z");
  const b = new Date(targetStr + "T00:00:00Z");
  return Math.round((b - a) / 86400000);
};

const FILTERS = [
  { key: "all", label: "Todos" },
  { key: "overdue", label: "Atrasados" },
  { key: "today", label: "Hoy" },
  { key: "week", label: "Esta semana" },
  { key: "month", label: "Este mes" },
  { key: "no_date", label: "Sin fecha" },
];

export default function AdminAgenda() {
  const [prospects, setProspects] = useState([]);
  const [filter, setFilter] = useState("all");
  const [excludeClosed, setExcludeClosed] = useState(true);
  const [search, setSearch] = useState("");
  const confirm = useConfirm();
  const { activeId, active, isAll, companies } = useCompany();
  const companyById = useMemo(
    () => companies.reduce((acc, c) => { acc[c.id] = c; return acc; }, {}),
    [companies],
  );

  useEffect(() => {
    const params = !isAll && activeId ? { company_id: activeId } : {};
    api.get("/admin/prospects", { params }).then((r) => setProspects(r.data)).catch(() => toast.error("Error cargando prospectos"));
  }, [activeId, isAll]);

  // Only prospects with a next_action defined (otherwise nothing to track)
  const withAction = useMemo(
    () => prospects.filter((p) => (p.next_action && p.next_action.trim()) || p.next_action_date),
    [prospects],
  );

  const filtered = useMemo(() => {
    let list = withAction;
    if (excludeClosed) list = list.filter((p) => p.stage !== "ganado" && p.stage !== "perdido");
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (p) =>
          (p.name || "").toLowerCase().includes(q) ||
          (p.company || "").toLowerCase().includes(q) ||
          (p.email || "").toLowerCase().includes(q) ||
          (p.next_action || "").toLowerCase().includes(q),
      );
    }
    list = list.filter((p) => {
      const d = daysFromToday(p.next_action_date);
      if (filter === "all") return true;
      if (filter === "no_date") return d === null;
      if (d === null) return false;
      if (filter === "overdue") return d < 0;
      if (filter === "today") return d === 0;
      if (filter === "week") return d >= 0 && d <= 7;
      if (filter === "month") return d >= 0 && d <= 30;
      return true;
    });
    // sort: overdue first (most overdue first), then nearest upcoming, then no-date last
    list = [...list].sort((a, b) => {
      const da = daysFromToday(a.next_action_date);
      const db = daysFromToday(b.next_action_date);
      if (da === null && db === null) return 0;
      if (da === null) return 1;
      if (db === null) return -1;
      return da - db;
    });
    return list;
  }, [withAction, filter, excludeClosed, search]);

  const counts = useMemo(() => {
    const base = excludeClosed
      ? withAction.filter((p) => p.stage !== "ganado" && p.stage !== "perdido")
      : withAction;
    const c = { all: base.length, overdue: 0, today: 0, week: 0, month: 0, no_date: 0 };
    base.forEach((p) => {
      const d = daysFromToday(p.next_action_date);
      if (d === null) c.no_date++;
      else if (d < 0) c.overdue++;
      else if (d === 0) c.today++;
      if (d !== null && d >= 0 && d <= 7) c.week++;
      if (d !== null && d >= 0 && d <= 30) c.month++;
    });
    return c;
  }, [withAction, excludeClosed]);

  const exportXLSX = () => {
    const rows = filtered.map((p) => {
      const d = daysFromToday(p.next_action_date);
      return {
        "Fecha próxima acción": formatDate(p.next_action_date),
        "Días": d === null ? "" : d,
        "Estado": d === null ? "Sin fecha" : d < 0 ? `Atrasado ${Math.abs(d)}d` : d === 0 ? "Hoy" : `En ${d}d`,
        "Cuenta": p.company_id && companyById[p.company_id] ? companyById[p.company_id].name : "",
        "Próxima acción": p.next_action || "",
        "Nombre": p.name || "",
        "Cargo": p.position || "",
        "Empresa": p.company || "",
        "Email": p.email || "",
        "Teléfono": p.phone || "",
        "Etapa": STAGE_LABEL[p.stage] || p.stage || "",
        "Valor estimado": Number(p.value) || 0,
        "Valor mensual": Number(p.value_monthly) || 0,
        "Moneda": p.currency || "",
        "Último contacto": formatDate(p.last_contact_at),
        "Notas": (p.notes || "").replace(/\n/g, " "),
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    // Column widths
    ws["!cols"] = [
      { wch: 14 }, { wch: 6 }, { wch: 14 }, { wch: 36 }, { wch: 22 }, { wch: 18 },
      { wch: 22 }, { wch: 28 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 8 },
      { wch: 14 }, { wch: 40 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Agenda");
    XLSX.writeFile(wb, `agenda-compromisos-${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success("Reporte descargado");
  };

  const markDone = async (id) => {
    const ok = await confirm({
      title: "¿Marcar como cumplido?",
      message: "Se eliminará la próxima acción y se registrará la fecha de último contacto.",
      confirmLabel: "Marcar cumplido",
      variant: "primary",
    });
    if (!ok) return;
    try {
      await api.patch(`/admin/prospects/${id}`, {
        next_action: "",
        next_action_date: null,
        last_contact_at: new Date().toISOString(),
      });
      toast.success("Compromiso cumplido");
      const params = !isAll && activeId ? { company_id: activeId } : {};
      const r = await api.get("/admin/prospects", { params });
      setProspects(r.data);
    } catch (e) {
      toast.error("Error al actualizar");
    }
  };

  return (
    <div data-testid="admin-agenda-page" className="p-6 md:p-8 print:p-0">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6 print:hidden">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-mono mb-1">
            Reporte{active ? ` · ${active.name}` : ""}
          </div>
          <h1 className="font-display font-bold text-2xl md:text-3xl text-ash-100">Agenda de Compromisos</h1>
          <p className="text-ash-300 text-sm mt-2">
            Próximas acciones y seguimientos planificados con los prospectos. Para que ninguno se escape.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <CompanySwitcher />
          <button
            data-testid="agenda-export-xlsx"
            onClick={exportXLSX}
            className="inline-flex items-center gap-2 bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-5 py-2.5 uppercase tracking-widest text-xs"
          >
            <Download size={14} /> Exportar Excel
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6 print:hidden">
        <KPI label="Atrasados" value={counts.overdue} accent="#9B1C1C" icon={AlertTriangle} />
        <KPI label="Hoy" value={counts.today} accent="#D9A557" icon={CalendarClock} />
        <KPI label="Esta semana" value={counts.week} accent="#5B8FAF" />
        <KPI label="Este mes" value={counts.month} accent="#7BA6B0" />
        <KPI label="Sin fecha" value={counts.no_date} accent="#9BA7B3" />
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4 print:hidden">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`agenda-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-2 text-[11px] uppercase tracking-widest border transition-colors ${
                filter === f.key
                  ? "bg-brand text-ink-800 border-brand font-semibold"
                  : "border-ink-500 text-ash-100 hover:border-brand"
              }`}
            >
              {f.label} <span className="font-mono opacity-70 ml-1">({counts[f.key]})</span>
            </button>
          ))}
        </div>
        <div className="md:ml-auto flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-xs text-ash-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={excludeClosed}
              onChange={(e) => setExcludeClosed(e.target.checked)}
              className="accent-brand"
            />
            Ocultar ganados/perdidos
          </label>
          <input
            data-testid="agenda-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar prospecto, empresa, acción..."
            className="bg-ink-900 border border-ink-500 text-ash-100 focus:border-brand outline-none px-3 py-2 text-sm placeholder:text-ash-500 w-72"
          />
        </div>
      </div>

      {/* Report header (only in print) */}
      <div className="hidden print:block mb-6 pb-4 border-b border-ash-100">
        <h1 className="font-bold text-2xl">DoSoft · Agenda de Compromisos</h1>
        <p className="text-sm">Generado: {formatDate(new Date())} · {filtered.length} compromisos</p>
      </div>

      {/* Table */}
      <div className="bg-ink-800 border border-ink-500 overflow-x-auto print:bg-white print:border-ash-100">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#4A555C] text-white print:bg-white print:text-black print:border-b print:border-ash-100">
              <Th>Fecha</Th>
              <Th>Días</Th>
              <Th>Cuenta</Th>
              <Th>Próxima acción</Th>
              <Th>Prospecto</Th>
              <Th>Empresa</Th>
              <Th>Contacto</Th>
              <Th>Etapa</Th>
              <Th className="text-right">Valor</Th>
              <Th className="text-right print:hidden">Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const d = daysFromToday(p.next_action_date);
              const isOverdue = d !== null && d < 0;
              const isToday = d === 0;
              return (
                <tr key={p.id} data-testid={`agenda-row-${p.id}`} className="border-t border-ink-500 hover:bg-ink-700/40 print:hover:bg-transparent">
                  <Td>
                    <div className="font-mono text-ash-100 print:text-black">{formatDate(p.next_action_date)}</div>
                  </Td>
                  <Td>
                    {d === null ? (
                      <span className="text-ash-500 text-xs">—</span>
                    ) : isOverdue ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-[#9B1C1C]/20 text-[#FF7A7A] border border-[#9B1C1C]/40 print:bg-transparent print:text-[#9B1C1C]">
                        <AlertTriangle size={10} /> {Math.abs(d)}d atraso
                      </span>
                    ) : isToday ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-[#D9A557]/20 text-[#FFD080] border border-[#D9A557]/40 print:bg-transparent print:text-[#8a6210]">
                        Hoy
                      </span>
                    ) : (
                      <span className="text-xs text-ash-300 print:text-black">en {d}d</span>
                    )}
                  </Td>
                  <Td>
                    {(() => {
                      const co = p.company_id ? companyById[p.company_id] : null;
                      if (!co) return <span className="text-ash-500 text-xs print:text-black">—</span>;
                      const col = co.color || "#7BA6B0";
                      return (
                        <span
                          className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold text-white shadow-sm print:bg-white print:text-black print:border print:border-ash-100"
                          style={{ background: col, boxShadow: `0 0 0 1px ${col}, 0 0 8px ${col}55` }}
                          title={`Cuenta: ${co.name}`}
                        >
                          <span className="w-1.5 h-1.5 bg-white/80" />
                          <span className="truncate max-w-[120px]">{co.name}</span>
                        </span>
                      );
                    })()}
                  </Td>
                  <Td>
                    <div className="text-ash-100 print:text-black">{p.next_action || <span className="text-ash-500">Sin acción</span>}</div>
                  </Td>
                  <Td>
                    <div className="font-medium text-ash-100 print:text-black">{p.name}</div>
                    {p.position && <div className="text-[11px] text-ash-500">{p.position}</div>}
                  </Td>
                  <Td>
                    <div className="text-ash-100 print:text-black">{p.company || "—"}</div>
                  </Td>
                  <Td>
                    <div className="space-y-0.5">
                      {p.email && (
                        <a href={`mailto:${p.email}`} className="flex items-center gap-1.5 text-[11px] text-ash-300 hover:text-brand print:text-black">
                          <Mail size={10} /> {p.email}
                        </a>
                      )}
                      {p.phone && (
                        <a href={`tel:${p.phone}`} className="flex items-center gap-1.5 text-[11px] text-ash-300 hover:text-brand print:text-black">
                          <Phone size={10} /> {p.phone}
                        </a>
                      )}
                      {!p.email && !p.phone && <span className="text-ash-500 text-xs">—</span>}
                    </div>
                  </Td>
                  <Td>
                    <span
                      className="inline-block px-2 py-1 text-[10px] uppercase tracking-widest font-semibold text-white print:bg-white print:text-black print:border print:border-ash-100"
                      style={{ backgroundColor: STAGE_COLOR[p.stage] || "#4A555C" }}
                    >
                      {STAGE_LABEL[p.stage] || p.stage}
                    </span>
                  </Td>
                  <Td className="text-right">
                    {Number(p.value) > 0 && (
                      <div className="font-mono text-brand print:text-black">{fmtMoney(p.value, p.currency)}</div>
                    )}
                    {Number(p.value_monthly) > 0 && (
                      <div className="font-mono text-[#D97757] print:text-black text-xs mt-0.5">
                        + {fmtMoney(p.value_monthly, p.currency)}/m
                      </div>
                    )}
                    {!Number(p.value) && !Number(p.value_monthly) && (
                      <span className="text-ash-500">—</span>
                    )}
                  </Td>
                  <Td className="text-right print:hidden">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => markDone(p.id)}
                        title="Marcar como cumplido"
                        className="p-1.5 border border-ink-500 hover:border-[#1B7E3F] hover:text-[#7CD49B] text-ash-300 transition-colors"
                      >
                        <CheckCircle2 size={13} />
                      </button>
                      <Link
                        to="/admin/crm"
                        title="Abrir en CRM"
                        className="p-1.5 border border-ink-500 hover:border-brand hover:text-brand text-ash-300 transition-colors"
                      >
                        <ExternalLink size={13} />
                      </Link>
                    </div>
                  </Td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center text-ash-500 text-sm py-12 print:text-black">
                  No hay compromisos en este filtro.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-ash-500 font-mono mt-3 print:text-black">
        Mostrando {filtered.length} de {withAction.length} compromisos · Horario Chile (UTC-4)
      </div>
    </div>
  );
}

function KPI({ label, value, accent, icon: Icon }) {
  return (
    <div className="bg-ink-800 border border-ink-500 p-4" style={{ borderTopColor: accent, borderTopWidth: 3 }}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-ash-300 font-mono">{label}</div>
        {Icon && <Icon size={14} style={{ color: accent }} />}
      </div>
      <div className="font-display font-bold text-3xl text-ash-100 mt-1">{value}</div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`px-4 py-3 text-left text-[10px] uppercase tracking-widest font-semibold ${className}`}>
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}
